import { convertTimeRange } from "@/helpers/time";
import type {
  Dataset,
  DatasetDetailSchemaResponse,
  DatasetFilter
} from "@/types/dataset";
import { EFilterOptionType } from "@/types/dataset";
import type {
  DatasetConfigAttributeResponse,
  GridConfigResponse
} from "@/types/project";
import type { AdminAreaSettings } from "@/types/spatial";

export type MappedFilter = {
  filterType: EFilterOptionType;
  filter: {
    criteria: string;
    include_nulls: boolean;
  } | null;
  attributeId: string;
  attributeCode: string | undefined;
  is_multiple_adm_area?: boolean;
};

export const DEFAULT_H3_CONFIG = {
  aggregation_type: "AVG",
  level: 8 // Default H3 resolution level
};

export const getMaxLevelAdminArea = (
  obj: AdminAreaSettings | null
): keyof AdminAreaSettings | null => {
  if (!obj) return null;
  let maxLevel: keyof AdminAreaSettings | null = null;

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const typedKey = key as keyof AdminAreaSettings;
      if (obj[typedKey] !== undefined && obj[typedKey] !== null) {
        maxLevel = typedKey;
      }
    }
  }

  return maxLevel;
};

export const mapDatasetFilters = (filter: DatasetFilter): MappedFilter => {
  let criteriaAndNulls = null;

  if (filter.filterType === EFilterOptionType.DATE_TIME) {
    criteriaAndNulls = {
      criteria:
        convertTimeRange(filter.dateTimeRange, filter.time_format) ?? "",
      include_nulls: filter.dateTimeRange?.includeNulls ?? false
    };
  } else if (filter.filterType === EFilterOptionType.MIN_MAX) {
    criteriaAndNulls = {
      criteria: `${filter.minMaxRange?.min}-${filter.minMaxRange?.max}`,
      include_nulls: filter.minMaxRange?.includeNulls ?? false
    };
  } else if (filter.filterType === EFilterOptionType.MULTIPLE) {
    const isAdminAreaFilter = filter.selectedOptions?.some((option) =>
      option.includes("::")
    );
    criteriaAndNulls = {
      criteria: filter.selectedOptions?.join("|") ?? "",
      include_nulls: false,
      ...(isAdminAreaFilter
        ? { is_multiple_adm_area: filter.is_multiple_adm_area ?? false }
        : {})
    };
  } else if (filter.filterType === EFilterOptionType.DATE) {
    criteriaAndNulls = {
      criteria: `${filter.dateRange?.from || null}/${filter.dateRange?.to || null}`,
      include_nulls: filter.dateRange?.includeNulls ?? false
    };
  } else if (filter.filterType === EFilterOptionType.TIME) {
    criteriaAndNulls = {
      criteria: `${filter.timeRange?.from || null}/${filter.timeRange?.to || null}`,
      include_nulls: filter.timeRange?.includeNulls ?? false
    };
  }

  return {
    filterType: filter.filterType,
    filter: criteriaAndNulls,
    attributeId: filter.paired_key_attribute_id ?? filter.attributeId,
    attributeCode: filter.attributeCode
  };
};

export const processCatchmentDatasetAttributes = (
  dataset: Dataset,
  filters: DatasetFilter[]
): DatasetConfigAttributeResponse[] => {
  const mappedFilters: MappedFilter[] = filters.map((filter) =>
    mapDatasetFilters(filter)
  );

  // Track matched filters to prevent the same filter from being matched to multiple attributes
  const matchedFilterKeys = new Set<string>();

  return (
    dataset.categories?.map((attribute) => {
      const attributeId = attribute.paired_key_attribute_id ?? attribute.id;
      const matchedFilter = mappedFilters.find((f) => {
        const filterKey = `${f.attributeId}_${f.attributeCode}`;
        if (matchedFilterKeys.has(filterKey)) return false;

        const isMatch = f.filter?.criteria?.includes("::")
          ? f.attributeId === attributeId
          : f.attributeId === attributeId &&
            f.attributeCode === attribute.attribute_code;

        return isMatch;
      }) || { filter: null, filterType: null };

      if (matchedFilter.filter) {
        const filterKey = `${(matchedFilter as MappedFilter).attributeId}_${(matchedFilter as MappedFilter).attributeCode}`;
        matchedFilterKeys.add(filterKey);
      }

      const { filter, filterType } = matchedFilter;
      return {
        filter,
        id: attributeId,
        weight_score: null,
        ...(filterType ? { filterType } : {}),
        ...(attribute.attribute_code
          ? { attribute_code: attribute.attribute_code }
          : {})
      };
    }) || []
  );
};

export const createFinalAttributes = (
  nonAdminAttributes: DatasetConfigAttributeResponse[],
  adminAreaAttributes: Map<string, DatasetConfigAttributeResponse>
): DatasetConfigAttributeResponse[] => {
  const finalAttributes = [...nonAdminAttributes];

  if (adminAreaAttributes.size > 0) {
    const sortedLevels = Array.from(adminAreaAttributes.keys()).sort((a, b) =>
      a.localeCompare(b)
    );

    const primaryAdminAttr = adminAreaAttributes.get(sortedLevels[0]);
    if (!primaryAdminAttr) return finalAttributes;

    const maxWeightScore = Math.max(
      ...Array.from(adminAreaAttributes.values()).map(
        (attr) => attr.weight_score || 0
      )
    );
    finalAttributes.push({
      ...primaryAdminAttr,
      id: primaryAdminAttr.id,
      weight_score: maxWeightScore
    });
  }

  return finalAttributes;
};

export const separateAdminAreaAttributes = (
  combinedAttributes: DatasetConfigAttributeResponse[],
  primaryDataset: Dataset
): {
  adminAreaAttributes: Map<string, DatasetConfigAttributeResponse>;
  nonAdminAttributes: DatasetConfigAttributeResponse[];
} => {
  const adminAreaAttributes = new Map<string, DatasetConfigAttributeResponse>();
  const nonAdminAttributes: DatasetConfigAttributeResponse[] = [];

  const isH3Dataset =
    primaryDataset.properties.spatialType === "H3" &&
    primaryDataset.data.source === "bvt";

  for (const attr of combinedAttributes) {
    let category: DatasetDetailSchemaResponse | undefined;
    if (isH3Dataset) {
      category = primaryDataset.data.schema.find(
        (item) => (item.paired_key_attribute_id ?? item.id) === attr.id
      );
    } else {
      category = primaryDataset.categories.find(
        (item) => (item.paired_key_attribute_id ?? item.id) === attr.id
      );
    }
    const attributeCode = category?.attribute_code;

    if (attributeCode?.startsWith("adm_area_level_")) {
      adminAreaAttributes.set(attributeCode, attr);
    } else {
      nonAdminAttributes.push(attr);
    }
  }

  return { adminAreaAttributes, nonAdminAttributes };
};

export const getGridConfig = (
  currentDataset: Dataset
): GridConfigResponse | undefined => {
  if (currentDataset?.h3Configuration) {
    return {
      aggregation_type:
        currentDataset?.h3Configuration?.aggregationType ?? "AVG",
      level: currentDataset?.h3Configuration?.resolution ?? 8
    };
  }

  return currentDataset?.properties?.spatialType === "H3" &&
    currentDataset?.data?.source === "bvt"
    ? DEFAULT_H3_CONFIG
    : undefined;
};
